<template>
  <page-layout
      :title="pageTitle"
      :breadcrumb="pageBreadcrumb"
      :info-cards="pageInfoCards"
  >
    <template #actions>
      <md-button class="md-primary md-round" @click="openCreate">
        <md-icon>add</md-icon>
        Add Area
      </md-button>
      <md-button class="md-primary md-round" @click="syncAreas">
        <md-icon>sync</md-icon>
        Sync Area
      </md-button>
      <md-button class="md-info md-round" @click="refreshList" :disabled="isLoading">
        <md-icon>refresh</md-icon>
        Refresh
      </md-button>
    </template>

    <template #content>
      <!-- Filters -->
      <div class="filters-bar">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-size-40 md-small-size-100">
            <md-field>
              <label>Station</label>
              <md-select v-model="filters.station_id" @md-selected="onFilterChange">
                <md-option value="">All Stations</md-option>
                <md-option value="__unassigned__">Unassigned</md-option>
                <md-option
                    v-for="station in stations"
                    :key="station.id"
                    :value="station.id"
                >
                  {{ station.name }}
                </md-option>
              </md-select>
            </md-field>
          </div>

          <div class="md-layout-item md-size-60 md-small-size-100"></div>
        </div>
      </div>

      <!-- Data Table -->
      <table-data
          ref="table"
          title="Biotime Areas"
          :columns="columns"
          :data="table"
          :total="total"
          :loading="isLoading"
          loading-text="Loading areas..."
          :searchable="true"
          search-placeholder="Search by area code or name..."
          storage-key="biotime-areas-list"
          empty-message="No areas found"
          @fetch="onTableFetch"
      >
        <template #toolbar-right>
          <md-button class="md-primary md-round" @click="openCreate">
            <md-icon>add</md-icon>
            Add Area
          </md-button>
        </template>

        <template #cell-area_code="{ item }">
          <strong class="area-code">{{ item.area_code }}</strong>
        </template>

        <template #cell-area_name="{ item }">
          {{ item.area_name }}
        </template>

        <template #cell-parent_area="{ item }">
          <span v-if="item.parent_area">{{ item.parent_area }}</span>
          <span v-else class="text-muted">—</span>
        </template>

        <template #cell-station_name="{ item }">
          <chip :type="item.station ? 'info' : 'danger'" :icon="item.station ? 'business' : 'help_outline'">
            {{ item.station ? item.station.name : 'Unassigned' }}
          </chip>
        </template>

        <template #cell-created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #actions="{ item }">
          <md-button class="md-icon-button md-primary" @click="openEdit(item)" title="Edit">
            <md-icon>edit</md-icon>
          </md-button>
          <md-button class="md-icon-button md-danger" @click="confirmDelete(item)" title="Delete">
            <md-icon>delete</md-icon>
          </md-button>
        </template>

        <template #empty-actions>
          <md-button class="md-primary md-round" @click="openCreate">
            <md-icon>add</md-icon>
            Add First Area
          </md-button>
        </template>
      </table-data>
      <biotime-area-form-modal
          v-model="modal.active"
          :item="modal.item"
          :stations="stations"
          @success="onModalSuccess"
          @error="onModalError"
          @closed="onModalClosed"
      />
    </template>

    <!-- Add/Edit Modal -->

  </page-layout>
</template>

<script>
import PageLayout from '@/components/Layouts/PageLayout.vue';
import TableData from '@/components/DataTable/TableData.vue';
import BiotimeAreaFormModal from './BiotimeAreaFormModal.vue';
import PageMixin from '@/mixins/page-mixin';
import Swal from 'sweetalert2';
import moment from 'moment';

export default {
  name: 'BiotimeAreasList',
  components: {
    PageLayout: PageLayout,
    TableData: TableData,
    BiotimeAreaFormModal: BiotimeAreaFormModal
  },
  mixins: [PageMixin],
  data() {
    return {
      table: [],
      total: 0,
      isLoading: false,
      stations: [],
      filters: {
        station_id: ''
      },
      modal: {
        active: false,
        item: null
      },
      columns: [
        {field: 'area_code', label: 'Area Code'},
        {field: 'area_name', label: 'Area Name'},
        {field: 'parent_area', label: 'Parent Area'},
        {field: 'station_name', label: 'Station'},
        {field: 'created_at', label: 'Created'}
      ],
      tableState: {
        page: 1,
        perPage: 15,
        search: ''
      }
    };
  },
  computed: {
    pageTitle() {
      return 'Biotime Areas';
    },
    pageBreadcrumb() {
      return [
        {label: 'Settings', path: '/settings'},
        {label: 'Biotime Areas'}
      ];
    },
    pageInfoCards() {
      var total = this.total;
      var assigned = 0;
      var unassigned = 0;

      this.table.forEach(function (area) {
        if (area.station_id) assigned++;
        else unassigned++;
      });

      return [
        {icon: 'list_alt', label: 'Total Areas', value: total, color: 'primary'},
        {icon: 'business', label: 'Assigned', value: assigned, color: 'success'},
        {icon: 'help_outline', label: 'Unassigned', value: unassigned, color: 'warning'},
        {icon: 'location_on', label: 'Stations', value: this.stations.length, color: 'info'}
      ];
    }
  },
  created() {
    this.fetchStations();
  },
  methods: {
    // ============ DATA ============
    async fetchStations() {
      try {
        await this.$store.dispatch('stations/list', {page: {size: 1000}});
        this.stations = this.$store.getters['stations/list'] || [];
      } catch (e) {
        console.error('Failed to load stations', e);
      }
    },

    async onTableFetch(state) {
      this.tableState = state;
      await this.fetchList();
    },

    async fetchList() {
      this.isLoading = true;
      try {
        var params = {
          include: 'station',
          filter: {},
          page: {
            number: this.tableState.page,
            size: this.tableState.perPage
          }
        };

        if (this.tableState.search) params.filter.search = this.tableState.search;

        if (this.filters.station_id === '__unassigned__') {
          params.filter.station_id = 'unassigned';
        } else if (this.filters.station_id) {
          params.filter.station_id = this.filters.station_id;
        }

        await this.$store.dispatch('biotimeAreas/list', params);
        this.table = this.$store.getters['biotimeAreas/list'] || [];
        this.total = this.$store.getters['biotimeAreas/listTotal'] || 0;
      } catch (error) {
        console.error('Error fetching areas:', error);
        this.$store.dispatch('alerts/error', 'Failed to load areas');
        this.table = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    onFilterChange() {
      if (this.$refs.table) {
        this.$refs.table.currentPage = 1;
        this.$refs.table.persistState();
        this.$refs.table.emitFetch();
      }
    },

    refreshList() {
      this.fetchList();
    },
    async syncAreas() {
      try {
        await this.$store.dispatch('biotimeAreas/syncAreas');
        this.notifySuccess('Biotime Areas Syncs');
      } catch (error) {
        this.notifyException(error)
      }

    },
    // ============ MODAL ============
    openCreate() {
      this.modal.item = null;
      this.modal.active = true;
    },

    openEdit(item) {
      this.modal.item = item;
      this.modal.active = true;
    },

    onModalSuccess() {
      this.fetchList();
    },

    onModalError(error) {
      console.error('Modal error:', error);
    },

    onModalClosed() {
      this.modal.item = null;
    },

    // ============ DELETE ============
    confirmDelete(item) {
      var self = this;
      Swal.fire({
        title: 'Delete Area?',
        html: '<p>Delete area <strong>' + item.area_name + '</strong> (' + item.area_code + ')?</p>' +
            '<p style="color: #666; font-size: 13px;">This action cannot be undone.</p>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete',
        cancelButtonText: 'Cancel'
      }).then(async function (result) {
        if (!result.value) return;
        try {
          await self.$store.dispatch('biotimeAreas/destroy', item.id);
          self.$store.dispatch('alerts/success', 'Area deleted successfully');
          await self.fetchList();
        } catch (error) {
          self.$store.dispatch('alerts/error', 'Failed to delete area');
        }
      });
    },

    // ============ HELPERS ============
    formatDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('DD MMM YYYY');
    }
  }
};
</script>

<style lang="scss" scoped>
.filters-bar {
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 20px;
}

.text-muted {
  color: #999;
}

.unassigned-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  background: #fff3e0;
  color: #e65100;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;

  .md-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
  }
}
</style>